/**
 * Veli portalının sunucu istemcisi.
 *
 * Oturum httpOnly çerezle taşınıyor; jeton JavaScript'e verilmiyor.
 */

export class ApiHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
  ) {
    super(kod)
  }
}

async function istek<T>(yol: string, secenek: RequestInit = {}): Promise<T> {
  const r = await fetch(yol, {
    ...secenek,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(secenek.headers ?? {}) },
  })
  const metin = await r.text()
  const govde = metin ? (JSON.parse(metin) as Record<string, unknown>) : {}
  if (!r.ok) throw new ApiHatasi(r.status, String(govde['hata'] ?? 'bilinmeyen'))
  return govde as T
}

export type Kanal = 'sms' | 'eposta'

/** Girilen değeri sunucunun beklediği alana koyar */
const kanalGovdesi = (kanal: Kanal, deger: string) =>
  kanal === 'eposta' ? { eposta: deger } : { telefon: deger }

export const api = {
  kodIste: (kanal: Kanal, deger: string) =>
    istek<{
      gonderildi: boolean
      kanal: Kanal
      maske: string
      gecerlilikSn: number
      /** Yalnızca deneme kurulumunda dolu: gerçek SMS sağlayıcısı yok */
      denemeKodu?: string
    }>('/api/veli/kod-iste', {
      method: 'POST',
      body: JSON.stringify(kanalGovdesi(kanal, deger)),
    }),
  giris: (kanal: Kanal, deger: string, kod: string) =>
    istek<{ veli: Veli }>('/api/veli/giris', {
      method: 'POST',
      body: JSON.stringify({ ...kanalGovdesi(kanal, deger), kod }),
    }),
  baglantiGirisi: (jeton: string) =>
    istek<{ veli: Veli }>('/api/veli/baglanti-girisi', {
      method: 'POST',
      body: JSON.stringify({ jeton }),
    }),
  cikis: () => istek<{ tamam: boolean }>('/api/veli/cikis', { method: 'POST' }),
  ben: () => istek<{ veli: Veli }>('/api/veli/ben'),

  cocuklar: () => istek<{ cocuklar: Cocuk[] }>('/api/veli/cocuklar'),
  odevler: (cocukId: string) =>
    istek<{ odevler: Odev[] }>(`/api/veli/cocuk/${cocukId}/odevler`),
  odevYapildi: (odevId: string) =>
    istek<{ tamam: boolean }>(`/api/veli/odev/${odevId}/yapildi`, { method: 'POST' }),
  randevular: (cocukId: string) =>
    istek<{ randevular: Randevu[] }>(`/api/veli/cocuk/${cocukId}/randevular`),
  raporlar: (cocukId: string) =>
    istek<{ raporlar: RaporOzet[] }>(`/api/veli/cocuk/${cocukId}/raporlar`),
  rapor: (raporId: string) => istek<{ rapor: RaporTam }>(`/api/veli/rapor/${raporId}`),

  formlar: (cocukId: string) =>
    istek<{ formlar: FormIstegi[]; tanimlar: Record<string, FormTanimi> }>(
      `/api/veli/cocuk/${cocukId}/formlar`,
    ),
  /**
   * Form kodu GÖNDERİLMİYOR: hangi form olduğu isteğin kendisinde
   * yazılı ve sunucu oradan okuyor. İstemcinin söylediği koda göre
   * puanlamak, cevaplarla kaydın ayrışması demekti.
   */
  formGonder: (istekId: string, cevaplar: Record<string, number>) =>
    istek<{ tamam: boolean }>(`/api/veli/formlar/${istekId}`, {
      method: 'POST',
      body: JSON.stringify({ cevaplar }),
    }),

  davranislar: (cocukId: string) =>
    istek<{ hedefler: DavranisHedefi[]; buHafta: string }>(
      `/api/veli/cocuk/${cocukId}/davranislar`,
    ),
  davranisYaz: (hedefId: string, cocukId: string, hafta: string, deger: number, not?: string) =>
    istek<{ kaydedildi: boolean }>(`/api/veli/davranis/${hedefId}`, {
      method: 'PUT',
      body: JSON.stringify({ cocukId, hafta, deger, not }),
    }),
}

/* ------------------------------------------------------------------ */

export interface Veli {
  adSoyad: string
}

export interface Cocuk {
  id: string
  ad: string
  soyad: string
  dogumTarihi: string
}

export interface Odev {
  id: string
  kod: string
  alan: string
  kanal: string
  sonTarih: string | null
  hafta: number
  yapildi: string | null
}

export interface Randevu {
  id: string
  baslangic: string
  bitis: string
  durum: string
  uygulayici: string | null
}

export interface RaporOzet {
  id: string
  donemBas: string
  donemBit: string
  gonderildi: string
}

export interface RaporTam extends RaporOzet {
  icerik: Record<string, unknown>
}

export interface FormIstegi {
  id: string
  kod: string
  istendi: string
  not: string | null
}

/**
 * Velinin dolduracağı davranış hedefi.
 *
 * Başlangıç (baseline) DÖNMÜYOR: klinik bir karşılaştırma noktası ve
 * veliye tek başına bir şey söylemiyor. Hedef dönüyor, çünkü çocuğun
 * hedefi aileyle birlikte konuşuluyor.
 *
 * Başka kaynakların (öğretmen, uygulayıcı) aynı hafta için girdiği
 * sayı da dönmüyor: onu görmek, tarafları birbirinin cevabına göre
 * ayarlamaya iter.
 */
export interface DavranisHedefi {
  id: string
  metin: string
  birim: string
  yon: 'lower' | 'higher'
  hedef: number | null
  kayitlar: { hafta: string; deger: number; not: string | null }[]
}

export interface FormTanimi {
  kod: string
  ad: string
  aciklama: string
  secenekler: string[]
  altBasliklar: Record<string, string>
  maddeler: { anahtar: string; metin: string; alt: string; ters?: boolean }[]
}

/** Egzersiz kodlarının velinin anlayacağı karşılığı */
export const EGZERSIZ_ADI: Record<string, string> = {
  scan_letters: 'Harf tarama',
  symbol_code: 'Şekil–sembol eşleme',
  symbol_match: 'Şekil eşleme',
  maze: 'Labirent',
  span_visual: 'Kutu sırası',
  span_recall: 'Dizi hatırlama',
  path_trace: 'Yol takibi',
  memory_pairs: 'Eş bulma',
  category_sort: 'Gruplama',
  odd_one_out: 'Farkı bul',
  word_search: 'Kelime bulmaca',
  sudoku: 'Sayı bulmaca',
  listening_steps: 'Yönerge takibi',
  number_add: 'Toplama',
}

export const ALAN_ADI: Record<string, string> = {
  sustained_attention: 'Dikkati sürdürme',
  selective_attention: 'Seçici dikkat',
  working_memory_visual: 'Görsel bellek',
  working_memory_verbal: 'Sözel bellek',
  processing_speed: 'İşlem hızı',
  inhibition: 'Dürtü kontrolü',
  cognitive_flexibility: 'Bilişsel esneklik',
}

export const RANDEVU_DURUMU: Record<string, string> = {
  scheduled: 'Planlandı',
  attended: 'Gelindi',
  no_show: 'Gelinmedi',
  cancelled: 'İptal edildi',
}

export const KANAL_ADI: Record<string, string> = {
  print: 'Kâğıt',
  digital: 'Tablet / bilgisayar',
  both: 'Kâğıt veya tablet',
}
